import React, { useState, useEffect, useRef } from 'react';
import { onOnlineUsersUpdate, searchPlayer, sendGameInvitation } from '../utils/socketUtils';
import countries from '../utils/countryData';
import '../styles/OnlineUsers.css';

// Custom event for pausing/resuming gameplay
export const pauseGameEvent = new CustomEvent('pauseGameplay', { detail: { paused: true } });
export const resumeGameEvent = new CustomEvent('pauseGameplay', { detail: { paused: false } });

// Refresh interval in milliseconds (30 seconds)
const REFRESH_INTERVAL = 30 * 1000;

// Safe localStorage access
const safeLocalStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('Error accessing localStorage:', error);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.warn('Error writing to localStorage:', error);
      return false;
    }
  }
};

// Safe JSON parsing
const safeJsonParse = (jsonString, defaultValue = null) => {
  if (!jsonString) return defaultValue;
  
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn('Error parsing JSON:', error);
    return defaultValue;
  }
};

const OnlineUsers = ({ currentUser }) => {
  const [onlineCount, setOnlineCount] = useState(0);
  const [showUsersList, setShowUsersList] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(Date.now());
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  const [nextRefreshIn, setNextRefreshIn] = useState(REFRESH_INTERVAL / 1000);
  const searchInputRef = useRef(null);
  const modalRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  
  // Get country flag emoji for a country name
  const getCountryFlag = (countryName) => {
    const country = countries.find(c => c.name === countryName);
    return country ? country.flag : '🏳️';
  };
  
  // Function to refresh online users data
  const refreshOnlineUsers = () => {
    // Update the lastRefreshed timestamp to trigger a re-render
    setLastRefreshed(Date.now());
    // Reset the countdown
    setNextRefreshIn(REFRESH_INTERVAL / 1000);
    // This will trigger the onOnlineUsersUpdate callback to be called again
    onOnlineUsersUpdate((data) => {
      if (isComponentMounted) {
        setOnlineCount(data.count);
        setOnlineUsers(data.users || {});
      }
    });
  };
  
  // Update countdown timer every second
  useEffect(() => {
    if (showUsersList) {
      // Start countdown timer when modal is open
      countdownIntervalRef.current = setInterval(() => {
        setNextRefreshIn(prev => {
          const newValue = prev - 1;
          return newValue > 0 ? newValue : REFRESH_INTERVAL / 1000;
        });
      }, 1000);
    }
    
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [showUsersList]);
  
  // Initialize the component
  useEffect(() => {
    setIsComponentMounted(true);
    
    // Set up listener for online users updates from our mock socket
    const removeListener = onOnlineUsersUpdate((data) => {
      setOnlineCount(data.count);
      setOnlineUsers(data.users || {});
    });
    
    // Set up interval to refresh online users every 30 seconds
    refreshIntervalRef.current = setInterval(() => {
      refreshOnlineUsers();
    }, REFRESH_INTERVAL);
    
    // Immediate refresh on mount to ensure we have the latest data
    setTimeout(() => {
      if (isComponentMounted) {
        refreshOnlineUsers();
      }
    }, 500);
    
    // Clean up listener and interval on component unmount
    return () => {
      setIsComponentMounted(false);
      removeListener();
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, []);
  
  // Update user activity status periodically
  useEffect(() => {
    // Get current user data from localStorage
    const currentUserDataStr = safeLocalStorage.getItem('currentOnlineUser');
    const currentUserData = safeJsonParse(currentUserDataStr);
    
    // Update activity timestamp every 45 seconds to prevent timeout
    const activityInterval = setInterval(() => {
      if (currentUserData) {
        safeLocalStorage.setItem('currentOnlineUser', JSON.stringify({
          ...currentUserData,
          lastActive: new Date().toISOString()
        }));
      }
    }, 45 * 1000);
    
    return () => {
      clearInterval(activityInterval);
    };
  }, []);
  
  // Handle clicking outside the modal to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUsersList && modalRef.current && !modalRef.current.contains(event.target)) {
        closePopup();
      }
    };

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUsersList]);
  
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && showUsersList) {
        closePopup();
      }
    };
    
    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showUsersList]);
  
  useEffect(() => {
    // Auto-focus search input when modal opens
    if (showUsersList && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [showUsersList]);
  
  const toggleUsersList = () => {
    if (!showUsersList) {
      // Opening the modal
      setShowUsersList(true);
      window.dispatchEvent(pauseGameEvent);
      // Refresh users list when opening the modal
      refreshOnlineUsers();
    } else {
      // Closing the modal
      closePopup();
    }
  };
  
  const closePopup = () => {
    setIsClosing(true);
    window.dispatchEvent(resumeGameEvent);
    
    // Clear countdown interval when closing
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    
    // Allow animation to complete before hiding
    setTimeout(() => {
      setShowUsersList(false);
      setIsClosing(false);
      setSearchTerm('');
      setSearchResults(null);
      setIsSearching(false);
    }, 200);
  };
  
  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
    
    // If user clears the search, reset results
    if (e.target.value.trim() === '') {
      setSearchResults(null);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      setSearchResults(null);
      return;
    }
    
    setIsSearching(true);
    
    // Search for player by username
    const result = searchPlayer(searchTerm);
    
    // Also search in online users in case the searchPlayer function missed something
    if (!result) {
      // Case-insensitive search in online users
      const matchedUser = Object.entries(onlineUsers).find(([username]) => 
        username.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      if (matchedUser) {
        const [username, userData] = matchedUser;
        setSearchResults({...userData, username});
      } else {
        setSearchResults(null);
      }
    } else {
      setSearchResults(result);
    }
    
    setIsSearching(false);
  };
  
  const handleInvitePlayer = (username) => {
    if (!currentUser || !username) return;
    
    // Generate a unique game ID
    const gameId = `game_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    
    // Get the current user's username (could be twitterUsername or username depending on format)
    const currentUsername = currentUser.twitterUsername || currentUser.username;
    
    // Send game invitation
    sendGameInvitation({
      from: currentUsername,
      to: username,
      gameId,
      fromUserId: currentUser.id || Date.now().toString(),
      invitationId: `inv_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      fromUsername: currentUsername,
      toUsername: username
    });
    
    // Show feedback and close popup
    alert(`Invitation sent to ${username}. Waiting for response...`);
    closePopup();
  };
  
  // If no current user, don't show the component
  if (!currentUser) return null;
  
  return (
    <div className="online-users-container">
      <div 
        className="online-users-badge"
        onClick={toggleUsersList}
        title="Click to see who's online"
      >
        <div className="online-indicator"></div>
        <span>{onlineCount} {onlineCount === 1 ? 'player' : 'players'} online</span>
      </div>
      
      {showUsersList && (
        <>
          <div className={`popup-overlay ${isClosing ? 'closing' : ''}`}></div>
          <div 
            ref={modalRef}
            className={`online-users-list ${isClosing ? 'closing' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <button 
              className="close-popup-btn" 
              onClick={closePopup} 
              aria-label="Close"
            >
              ×
            </button>
            <h3 id="modal-title">Online Players</h3>
            
            {/* Search form */}
            <form onSubmit={handleSearch} className="user-search-form">
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchInputChange}
                placeholder="Search by username..."
                className="user-search-input"
                aria-label="Search players"
              />
              <button type="submit" className="user-search-btn">
                {isSearching ? 'Searching...' : 'Search'}
              </button>
            </form>
            
            {/* Search results */}
            {searchResults !== null && (
              <div className="search-results">
                <h4>Search Results</h4>
                {searchResults ? (
                  <div className="user-search-result">
                    <div className="user-online-indicator"></div>
                    <span className="user-name">
                      {searchResults.twitterUsername || searchResults.username}
                      {searchResults.country && (
                        <span className="user-country" title={searchResults.country}>
                          {" "}{getCountryFlag(searchResults.country)}
                        </span>
                      )}
                    </span>
                    {/* Commenting out invite button - not allowing users to use it yet
                    <button 
                      className="invite-btn"
                      onClick={() => handleInvitePlayer(searchResults.twitterUsername || searchResults.username)}
                    >
                      Invite
                    </button>
                    */}
                  </div>
                ) : (
                  <p>No user found with that username</p>
                )}
              </div>
            )}
            
            {/* List of online users */}
            {Object.keys(onlineUsers).length > 0 ? (
              <div>
                <h4>Currently Online</h4>
                <div className="refresh-info">
                  <small>
                    Last updated: {new Date(lastRefreshed).toLocaleTimeString()}
                    <br />
                    Next refresh in {nextRefreshIn} seconds
                  </small>
                  <button 
                    className="refresh-btn" 
                    onClick={(e) => {
                      e.preventDefault();
                      refreshOnlineUsers();
                    }}
                    title="Refresh now"
                    aria-label="Refresh online users"
                  >
                    ↻
                  </button>
                </div>
                <ul className="users-list">
                  {/* Show current user first */}
                  {(() => {
                    const currentUsername = currentUser?.twitterUsername || currentUser?.username;
                    if (currentUsername && onlineUsers[currentUsername]) {
                      const userData = onlineUsers[currentUsername];
                      return (
                        <li key={`current-${currentUsername}`} className="user-item current-user recently-active">
                          <div className="user-online-indicator pulse"></div>
                          <span className="user-name">
                            {currentUsername} <span className="current-user-label">(You)</span>
                            {userData.country && (
                              <span className="user-country" title={userData.country}>
                                {" "}{getCountryFlag(userData.country)}
                              </span>
                            )}
                            <small className="last-active-time">active now</small>
                          </span>
                        </li>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Show other online users */}
                  {Object.entries(onlineUsers).map(([username, userData]) => {
                    // Skip the current user as we already displayed them first
                    const currentUsername = currentUser?.twitterUsername || currentUser?.username;
                    if (username === currentUsername) return null;
                    
                    // Calculate how recently the user was active
                    const lastActive = new Date(userData.lastActive).getTime();
                    const activeAgo = Math.floor((Date.now() - lastActive) / 1000);
                    
                    // Skip inactive users (more than 60 seconds without activity)
                    if (activeAgo > 60) return null;
                    
                    const isVeryRecent = activeAgo < 30;
                    
                    return (
                      <li key={username} className={`user-item ${isVeryRecent ? 'recently-active' : ''}`}>
                        <div className={`user-online-indicator ${isVeryRecent ? 'pulse' : ''}`}></div>
                        <span className="user-name">
                          {username}
                          {userData.country && (
                            <span className="user-country" title={userData.country}>
                              {" "}{getCountryFlag(userData.country)}
                            </span>
                          )}
                          <small className="last-active-time">
                            {isVeryRecent ? 'active now' : `${activeAgo}s ago`}
                          </small>
                        </span>
                        {/* Commenting out invite button - not allowing users to use it yet
                        <button 
                          className="invite-btn"
                          onClick={() => handleInvitePlayer(username)}
                        >
                          Invite
                        </button>
                        */}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <p>No other players online</p>
            )}
            
            <div className="online-users-note">
              <p>
                <small>
                  Game is paused while this window is open.<br />
                  Click "Invite" to challenge a player.
                </small>
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OnlineUsers; 